/**
 * #605 — what the Papan host can observe of the person's recent input: the
 * half of "did the person just act in the app?" it can see (useHandoffRelay
 * `gestureInApp`).
 *
 * A press inside ANY frame activates this page but never reaches this
 * document, and moving focus from one frame to another fires nothing here
 * (Chromium: a click into a frame raises no `focusin` on its element). So the
 * tracker hears presses where it CAN:
 *   - Papan's own document (capture phase, every activating event);
 *   - every SAME-ORIGIN frame — Papan's own UI in a frame: the Accounts bar,
 *     the picker page — whose document it joins as each one loads.
 * A cross-origin frame (the app itself, another app, an office editor) stays
 * unheard; the relay reads where focus is instead — exempting only Papan's own
 * UI frames (`PAPAN_FRAME_ATTR` + heard: `isOwnFrame`).
 */

/**
 * Every event that can activate the page (the HTML spec's list: `keydown`,
 * `mousedown`, `pointerdown`, a non-mouse `pointerup`, `touchend`) plus
 * `click` (assistive tech often sends only that).
 */
const ACTIVATING_EVENTS = ['keydown', 'mousedown', 'pointerdown', 'pointerup', 'touchend', 'click'] as const

/**
 * Marks an iframe as PAPAN'S OWN UI (the Accounts bar, the picker page). Only
 * a marked frame whose document the tracker hears counts as Papan's own —
 * "same-origin" alone would also take in a frame wrapping someone else's
 * content (a `blob:` PDF, an editor host), whose presses are not heard.
 */
export const PAPAN_FRAME_ATTR = 'data-papan-frame'

export interface InputTracker {
  /** When input last reached Papan — its document or a same-origin frame's
   *  (ms, `now()`'s clock). */
  lastInputAt(): number
  /** Papan's own UI in a frame: marked (`PAPAN_FRAME_ATTR`), and its document
   *  heard by the tracker. */
  isOwnFrame(frame: Element): boolean
}

/**
 * Track input on `doc` and its same-origin frames. What happened BEFORE this
 * ran is unknown — the click that opened the first viewer among it — so
 * starting counts as input now: unknown history falls toward caution, never
 * toward trusting the app.
 */
export function createInputTracker(doc: Document, now: () => number = () => Date.now()): InputTracker {
  let last = now()
  const note = () => { last = now() }
  const heard = new WeakSet<Document>()
  const ownFrames = new WeakSet<Element>()

  const listen = (d: Document) => {
    if (heard.has(d)) return
    heard.add(d)
    for (const type of ACTIVATING_EVENTS) d.addEventListener(type, note, true)
    // A load in THIS document's frames (nested Papan UI) joins them too.
    d.addEventListener('load', onLoad, true)
    for (const frame of Array.from(d.getElementsByTagName('iframe'))) join(frame)
  }
  /** Join a frame's current document, if it is same-origin — else it stays
   *  unheard, and is not Papan's (a frame's first, blank document can be
   *  readable before cross-origin content loads into it — a frame that is
   *  not sandboxed, or keeps `allow-same-origin`). */
  const join = (frame: Element) => {
    let inner: Document | null = null
    try {
      inner = (frame as HTMLIFrameElement).contentDocument
    } catch { /* cross-origin */ }
    if (!inner) {
      ownFrames.delete(frame)
      return
    }
    ownFrames.add(frame)
    listen(inner)
  }
  // `load` does not bubble, but a capture listener on the document still sees
  // every frame's — each navigation gives the frame a new document to join.
  function onLoad(e: Event) {
    const target = e.target as Element | null
    if (target?.tagName === 'IFRAME') join(target)
  }

  listen(doc)
  return {
    lastInputAt: () => last,
    isOwnFrame: (frame) => ownFrames.has(frame) && frame.hasAttribute(PAPAN_FRAME_ATTR),
  }
}

let shared: InputTracker | null = null

/** The page's one tracker, started on first use and kept for the page's life
 *  (every viewer shares it). Null where there is no document (SSR). */
export function papanInput(): InputTracker | null {
  if (typeof document === 'undefined') return null
  shared ??= createInputTracker(document)
  return shared
}
